/**
 * SmartWishlist SDK Ready Handler
 * Add promise control
 */
class SmartWishlistSDK {
  static readyPromise = null;
  static resolveReady = null;
  static ready = false;

  static {
    this.readyPromise = new Promise((resolve) => {
      this.resolveReady = resolve;
    });

    if (typeof window.SmartWishlistMain === 'function') {
      this.markAsReady();
    } else {
      window.addEventListener('smartwishlist:ready', () => this.markAsReady(), { once: true });
      this.observeScriptLoad();
    }
  }

  static markAsReady() {
    if (this.ready) return;
    this.ready = true;
    this.resolveReady();
  }

  static observeScriptLoad() {
    let smartWishlistMain = window.SmartWishlistMain;
    
    Object.defineProperty(window, 'SmartWishlistMain', {
      get: () => smartWishlistMain,
      set: (value) => {
        smartWishlistMain = value;
        if (typeof value === 'function') {
          this.markAsReady();
        }
      },
      configurable: true
    });
  }

  /**
   * return promise that it resolve when SDK is ready
   * @returns {Promise<void>}
   */
  static whenReady() {
    return this.readyPromise;
  }

  /**
   * @returns {boolean}
   */
  static get isReady() {
    return this.ready;
  }
}


/**
 * Utility class for management of wishlist
 */
class OpWishlistUtils {
  static syncPromise = null;

  /**
   * @returns {Array|null}
   */
  static get cachedWishlist() {
    const wl = localStorage.getItem(WISHLIST_CONSTANTS.localStorageKey);
    return wl ? JSON.parse(wl) : null;
  }

  /**
   * @returns {string|null}
   */
  static get currentCustomerId() {
    const customerId = ShopifyAnalytics?.meta?.page?.customerId;
    return customerId ? String(customerId) : null;
  }

  /**
   * @returns {string}
   */
  static get wishlistUrl() {
    const customerId = this.currentCustomerId;
    const baseUrl = `${Shopify.routes.root}a/wishlist?type=api&version=1`;
    
    return customerId
      ? `${baseUrl}&customerid=${customerId}`
      : `${baseUrl}&wishlistid=${this.wishlistId}`;
  }

  /**
   * @returns {string|null}
   */
  static get wishlistId() {
    const cookies = decodeURIComponent(document.cookie).split(';');
    const wishlistCookie = cookies.find(c => c.trim().startsWith('wishlist_id='));
    
    return wishlistCookie?.trim().substring('wishlist_id='.length) || null;
  }

  /**
   * @returns {string|null}
   */
  static get storedCustomerId() {
    return localStorage.getItem(WISHLIST_CONSTANTS.customerStorageKey);
  }

  /**
   * @param {string|null} customerId
   */
  static setStoredCustomerId(customerId) {
    if (customerId) {
      localStorage.setItem(WISHLIST_CONSTANTS.customerStorageKey, String(customerId));
    } else {
      localStorage.removeItem(WISHLIST_CONSTANTS.customerStorageKey);
    }
  }

  /**
   * @param {Array} wishlist
   */
  static setCachedWishlist(wishlist) {
    localStorage.setItem(WISHLIST_CONSTANTS.localStorageKey, JSON.stringify(wishlist || []));
  }

  static clearWishlistCache() {
    localStorage.removeItem(WISHLIST_CONSTANTS.localStorageKey);
  }

  static clearWishlistCookie() {
    const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = `wishlist_id=; ${expiry}; path=/`;
    document.cookie = `wishlist_id=; ${expiry}; path=/; domain=${window.location.hostname}`;
  }

  /**
   * ensure we keep wishlist in sync across login/logout
   */
  static async ensureWishlistSyncedWithCustomer() {
    if (this.syncPromise) return this.syncPromise;

    this.syncPromise = (async () => {
      try {
        const currentCustomerId = this.currentCustomerId;
        const storedCustomerId = this.storedCustomerId;

        if (currentCustomerId && !storedCustomerId) {
          await this.migrateGuestWishlistToCustomer(currentCustomerId);
        } else if (!currentCustomerId && storedCustomerId) {
          this.clearWishlistCache();
          this.setStoredCustomerId(null);
          this.clearWishlistCookie();
        } else if (currentCustomerId && storedCustomerId && storedCustomerId !== currentCustomerId) {
          this.clearWishlistCache();
          await this.syncCustomerWishlistFromServer(currentCustomerId);
        }
      } catch (error) {
        console.error('[Wishlist] Error syncing wishlist state:', error);
      }
    })();

    try {
      await this.syncPromise;
    } finally {
      this.syncPromise = null;
    }
  }

  /**
   * get wishlist form chache or server
   * @returns {Promise<Array>}
   */
  static async retrieveWishlist() {
    await this.ensureWishlistSyncedWithCustomer();
    return this.cachedWishlist ?? await this.fetchWishlist();
  }

  static async syncCustomerWishlistFromServer(currentCustomerId) {
    const wl = await this.fetchWishlist();
    this.setStoredCustomerId(currentCustomerId);
    return wl;
  }

  static mergeWishlistItems(primary = [], secondary = []) {
    const mergedMap = new Map();

    primary.forEach(item => mergedMap.set(item.variant_id, item));
    secondary.forEach(item => {
      if (!mergedMap.has(item.variant_id)) {
        mergedMap.set(item.variant_id, item);
      }
    });

    return Array.from(mergedMap.values());
  }

  static async migrateGuestWishlistToCustomer(currentCustomerId) {
    const guestWishlist = this.cachedWishlist || [];
    const customerWishlist = await this.fetchWishlist();

    if (guestWishlist.length === 0) {
      this.setStoredCustomerId(currentCustomerId);
      return customerWishlist;
    }

    const existingIds = new Set(customerWishlist.map(item => item.variant_id));
    const missingItems = guestWishlist.filter(item => !existingIds.has(item.variant_id));

    if (missingItems.length) {
      try {
        await SmartWishlistSDK.whenReady();

        for (const item of missingItems) {
          await this.callWishlistAPI(WISHLIST_CONSTANTS.eventKeyAdd, {
            productId: item.product_id,
            variantId: item.variant_id,
            productHandle: item.handle
          });
        }
      } catch (error) {
        console.error('[Wishlist] Error migrating wishlist to customer:', error);
      }
    }

    const mergedWishlist = this.mergeWishlistItems(customerWishlist, guestWishlist);
    this.setCachedWishlist(mergedWishlist);
    this.setStoredCustomerId(currentCustomerId);

    return mergedWishlist;
  }

  /**
   * Fetch from server
   * @returns {Promise<Array>}
   */
  static async fetchWishlist() {
    const customerId = this.currentCustomerId;

    if (!this.wishlistId && !customerId) {
      console.warn('[Wishlist] No wishlist ID or customer ID — skipping fetch.');
      return [];
    }

    try {
      const response = await fetch(this.wishlistUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const { items: wlItems = [] } = await response.json();
      this.setCachedWishlist(wlItems);
      if (customerId) {
        this.setStoredCustomerId(customerId);
      }
      
      return wlItems;
    } catch (error) {
      console.error('[Wishlist] Error fetching:', error);
      return this.cachedWishlist || [];
    }
  }

  /**
   * check if  variant is in wishlist page
   * @param {string} variantId
   * @returns {Promise<boolean>}
   */
  static async isItemInWishlist(variantId) {
    const wl = await this.retrieveWishlist();
    return wl.some(item => item.variant_id === variantId);
  }

  /**
   * update wishlist (add/remove)
   * @param {string} eventKey
   * @param {Object} item
   * @param {string} item.productId
   * @param {string} [item.productHandle]
   * @param {string} item.variantId
   * @returns {Promise<Array>}
   */
  static async updateWishlist(eventKey, item) {
    await this.ensureWishlistSyncedWithCustomer();

    const currentWl = this.applyOptimisticUpdate(eventKey, item);
    publish(PUB_SUB_EVENTS.updateWishlist, { wishlist: currentWl });

    this.callWishlistAPI(eventKey, item).catch(error => {
      console.error('[Wishlist] API call failed:', error);
    });
    
    return currentWl;
  }

  /**
   * apply optimistic update to local cache
   */
  static applyOptimisticUpdate(eventKey, item) {
    let currentWl = this.cachedWishlist || [];

    if (eventKey === WISHLIST_CONSTANTS.eventKeyAdd) {
      const exists = currentWl.some(wlItem => wlItem.variant_id === item.variantId);
      
      if (!exists) {
        currentWl = [...currentWl, {
          product_id: item.productId,
          variant_id: item.variantId,
          handle: item.productHandle
        }];
      }
    } else if (eventKey === WISHLIST_CONSTANTS.eventKeyRemove) {
      currentWl = currentWl.filter(wlItem => wlItem.variant_id !== item.variantId);
    }

    this.setCachedWishlist(currentWl);
    return currentWl;
  }

  /**
   * call 'API for SmartWishlist
   */
  static async callWishlistAPI(eventKey, item) {
    await SmartWishlistSDK.whenReady();

    const apiMap = {
      [WISHLIST_CONSTANTS.eventKeyAdd]: 'AddToSmartWishlist',
      [WISHLIST_CONSTANTS.eventKeyRemove]: 'RemoveFromSmartWishlist'
    };

    const fnName = apiMap[eventKey];
    
    if (fnName && typeof window[fnName] === 'function') {
      await window[fnName](item.productId, item.variantId);
    }
  }
}


/**
 * main compontent for wishlist page
 */
class OpWishlist extends HTMLElement {
  constructor() {
    super();
    this.customerId = OpWishlistUtils.currentCustomerId;
    this.wishlistData = [];
    this.unsubscribe = null;
  }

  get loader() {
    return this.querySelector('[data-wishlist-loader]');
  }

  get itemCountDisplay() {
    return this.querySelector('[data-wishlist-item-count]');
  }

  async connectedCallback() {
    await this.loadWishlist();
    
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.updateWishlist, () => {
      this.loadWishlist();
    });
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  async loadWishlist() {
    this.wishlistData = await OpWishlistUtils.retrieveWishlist();
    await this.renderWishlistItems();
  }

  hideLoader() {
    if (this.loader) {
      this.loader.style.display = 'none';
    }
  }

  async renderWishlistItems() {
    const emptyWrapper = this.querySelector('#wishlist-empty-wrapper');
    const productsWrapper = this.querySelector('#wishlist-products-wrapper');
    const container = this.querySelector('#wishlist-products');

    this.updateItemCount();

    if (this.wishlistData.length === 0) {
      emptyWrapper?.classList.remove('hidden');
      productsWrapper?.classList.add('hidden');
      this.hideLoader();
      return;
    }

    try {
      const fragment = await this.fetchWishlistItemsHTML();
      
      if (container) {
        container.innerHTML = '';
        container.appendChild(fragment);
        this.handleLoginCard(container);
      }

      emptyWrapper?.classList.add('hidden');
      productsWrapper?.classList.remove('hidden');
    } catch (error) {
      console.error('[Wishlist] Error loading items:', error);
    }

    this.hideLoader();
  }

  updateItemCount() {
    if (!this.itemCountDisplay) return;
    
    const count = this.wishlistData.length;
    this.itemCountDisplay.textContent = count > 0 ? `(${count})` : '';
    this.itemCountDisplay.toggleAttribute('data-visible', count > 0);
  }

  async fetchWishlistItemsHTML() {
    const fetchPromises = this.wishlistData.map(item =>
      fetch(`${Shopify.routes.root}products/${item.handle}/?sections=op-wishlist-item&variant=${item.variant_id}`)
    );

    const responses = await Promise.all(fetchPromises);
    const jsonResponses = await Promise.all(responses.map(res => res.json()));
    
    const fragment = document.createDocumentFragment();
    
    jsonResponses.forEach(response => {
      const html = new DOMParser().parseFromString(response['op-wishlist-item'], 'text/html');
      const wishlistItem = html.querySelector('[data-op-wishlist-item]');
      
      if (wishlistItem) {
        fragment.appendChild(wishlistItem);
      }
    });

    return fragment;
  }

  handleLoginCard(container) {
    if (!container || this.customerId) return;

    container.querySelector('.wishlist-login-card')?.remove();

    const loginCard = this.createLoginCard();
    if (!loginCard) return;

    const productItems = container.querySelectorAll('[data-op-wishlist-item]');
    const insertAfterIndex = Math.min(productItems.length, 2) - 1;
    
    if (insertAfterIndex >= 0) {
      productItems[insertAfterIndex].insertAdjacentElement('afterend', loginCard);
    } else {
      container.appendChild(loginCard);
    }

    this.initLoginCardModal(loginCard);
  }

  createLoginCard() {
    const template = document.getElementById('wishlist-login-template');
    if (!template) return null;

    const card = document.createElement('div');
    card.className = 'wishlist-login-card';
    card.innerHTML = template.innerHTML;
    
    return card;
  }

  initLoginCardModal(cardEl) {
    const loginModal = document.querySelector('#login-overlay-modal');
    if (!loginModal || typeof loginModal.showModal !== 'function') return;

    cardEl.querySelectorAll('[data-login-trigger]').forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        
        if (loginModal.dataset.position === 'right') {
          loginModal.classList.add('tw-animate-slide-left');
        }
        
        loginModal.showModal();
      });
    });
  }
}

if (!customElements.get('op-wishlist')) {
  customElements.define('op-wishlist', OpWishlist);
}


/**
 * Toggle Button add/remove in wishlist
 */
class OpWishlistToggleButton extends HTMLElement {
  constructor() {
    super();
    this.wishlistItem = {
      productId: this.dataset.productId,
      variantId: this.dataset.variantId,
      productHandle: this.dataset.handle,
      variantSku: this.dataset.sku
    };
    this.isInWishlist = false;
    this.unsubscribers = [];
  }

  async connectedCallback() {
    await this.init();
  }

  disconnectedCallback() {
    this.unsubscribers.forEach(unsub => unsub?.());
  }

  async init() {
    await this.syncWishlist();
    await this.enableWhenSDKReady();
    this.setupSubscriptions();
    this.setupEventListeners();
  }

  async enableWhenSDKReady() {
    await SmartWishlistSDK.whenReady();
    this.removeAttribute('disabled');
  }

  setupSubscriptions() {
    this.unsubscribers.push(
      subscribe(PUB_SUB_EVENTS.updateWishlist, () => this.checkWishlistItemPresence()),
      subscribe(PUB_SUB_EVENTS.variantChange, (payload) => this.handleVariantChange(payload))
    );
  }

  setupEventListeners() {
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  handleVariantChange(payload) {
    const { productId, variant } = payload.data;
    
    if (this.wishlistItem.productId !== productId) return;
    if (this.wishlistItem.variantId === String(variant.id)) return;

    this.wishlistItem.variantId = String(variant.id);
    this.checkWishlistItemPresence();
  }

  handleClick(evt) {
    evt.preventDefault();
    this.toggleAttribute('active');
    
    const eventKey = this.isInWishlist
      ? WISHLIST_CONSTANTS.eventKeyRemove
      : WISHLIST_CONSTANTS.eventKeyAdd;
    
    // Tracking Tealium per aggiunta wishlist
    if (eventKey === WISHLIST_CONSTANTS.eventKeyAdd) {
      this.sendTealiumTracking();
    }
    
    OpWishlistUtils.updateWishlist(eventKey, this.wishlistItem);
  }

  sendTealiumTracking() {
    // Recupera il prezzo da window.opTealiumPrices
    const productNinjaPrice = window.opTealiumPrices?.find(
      (item) => item.productHandle === this.wishlistItem.productHandle
    );

    // Invia dati a Tealium solo se il prezzo esiste
    if (productNinjaPrice) {
      const tealiumPrice = { ...productNinjaPrice };
      delete tealiumPrice.productHandle;

      // Funzione di formattazione
      const fmt = (val) => {
        const n = Number(val);
        return Number.isFinite(n) ? (n / 100).toFixed(2) : '';
      };

      const tealiumObj = {
        id: 'Event',
        Events_ProdFavAdd: '1',
        Products: {
          [this.wishlistItem.variantSku]: {
            Price: tealiumPrice?.price ? (Number(tealiumPrice.price) / 100).toFixed(2) : undefined,
            PriceFull: tealiumPrice?.priceFull ? (Number(tealiumPrice.priceFull) / 100).toFixed(2) : undefined,
          }
        }
      };

      if (typeof Fetchutils !== 'undefined' && typeof Fetchutils.sendTealiumData === 'function') {
        Fetchutils.sendTealiumData(tealiumObj);
      }
    }
  }

  handleKeydown(event) {
    if (document.activeElement !== this) return;
    
    if (['Enter', ' ', 'Spacebar'].includes(event.key)) {
      event.preventDefault();
      this.click();
    }
  }

  async checkWishlistItemPresence() {
    this.isInWishlist = await OpWishlistUtils.isItemInWishlist(this.wishlistItem.variantId);
    this.toggleAttribute('active', this.isInWishlist);
  }

  async syncWishlist() {
    const wl = await OpWishlistUtils.retrieveWishlist();
    this.isInWishlist = wl.some(item => item.variant_id === this.wishlistItem.variantId);
    this.toggleAttribute('active', this.isInWishlist);
  }
}

if (!customElements.get('op-wishlist-toggle-button')) {
  customElements.define('op-wishlist-toggle-button', OpWishlistToggleButton);
}


/**
 * Wishlist icon header
 */
class OpWishlistHeaderIcon extends HTMLElement {
  constructor() {
    super();
    this.url = this.dataset.url || '#';
    this.unsubscribe = null;
  }

  async connectedCallback() {
    await this.syncWishlist();
    
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.updateWishlist, ({ wishlist }) => {
      this.setAttribute('active', wishlist.length > 0);
    });

    this.addEventListener('click', () => {
      window.location = this.url;
    });
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  async syncWishlist() {
    const wl = await OpWishlistUtils.retrieveWishlist();
    this.setAttribute('active', wl.length > 0);
  }
}

if (!customElements.get('op-wishlist-header-icon')) {
  customElements.define('op-wishlist-header-icon', OpWishlistHeaderIcon);
}


// bfcache (back-forward cache)
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  
  document.querySelectorAll('op-wishlist-toggle-button').forEach(btn => btn.syncWishlist());
  document.querySelectorAll('op-wishlist-header-icon').forEach(icon => icon.syncWishlist());
});
